import { showError, showSuccess } from "./toast";

/**
 * Map API / thunk error payloads to a user-facing message.
 * Never treat declined/forbidden responses as success.
 */
export function getApiErrorMessage(errorOrPayload, fallback = "Something went wrong") {
  const status =
    errorOrPayload?.status ||
    errorOrPayload?.response?.status ||
    errorOrPayload?.payload?.status;

  // Prefer API body message/error over Axios' generic "Request failed with status code XXX"
  const data =
    errorOrPayload?.response?.data ||
    errorOrPayload?.payload?.response?.data ||
    errorOrPayload?.data;

  const raw =
    (typeof data === "string" ? data : null) ||
    data?.message ||
    data?.error ||
    data?.detail ||
    data?.msg ||
    errorOrPayload?.payload?.message ||
    errorOrPayload?.payload?.error ||
    (errorOrPayload?.message &&
    !/^request failed with status code \d+/i.test(errorOrPayload.message)
      ? errorOrPayload.message
      : null) ||
    errorOrPayload?.error;

  const message = typeof raw === "string" && raw.trim() ? raw.trim() : fallback;
  const accessDenied =
    status === 403 ||
    errorOrPayload?.accessDenied ||
    errorOrPayload?.payload?.accessDenied ||
    /access denied/i.test(String(message));

  if (accessDenied) {
    return message && !/^access denied$/i.test(message)
      ? message
      : "You are not authorized to perform this action";
  }
  if (status === 401) return message || "Please sign in again";
  if (status === 404) return message || "Resource not found";
  if (status === 400) return message || "Invalid request";
  if (status >= 500) return message || "Server error. Please try again later";
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return "Network error. Check your connection and try again";
  }

  return message || fallback;
}

/** Prefers API body text and prefixes HTTP status (e.g. "403: …") */
export function getNotificationErrorMessage(errorOrPayload, fallback) {
  const status =
    errorOrPayload?.status ||
    errorOrPayload?.response?.status ||
    errorOrPayload?.payload?.status;
  const message = getApiErrorMessage(errorOrPayload, fallback);
  if (status && message && !String(message).startsWith(`${status}`)) {
    return `${status}: ${message}`;
  }
  return message;
}

export function isForbiddenError(errorOrPayload) {
  const status =
    errorOrPayload?.status ||
    errorOrPayload?.response?.status ||
    errorOrPayload?.payload?.status;
  return (
    status === 403 ||
    Boolean(errorOrPayload?.accessDenied) ||
    Boolean(errorOrPayload?.payload?.accessDenied)
  );
}

/**
 * Show error toast for any failed API status.
 * Format: "<status>: <API message>" (deduped in showError).
 */
export function notifyApiError(errorOrPayload, fallback) {
  showError(
    getNotificationErrorMessage(
      errorOrPayload,
      fallback || "Request failed"
    )
  );
}

/** Dedicated 403 toast — use when you only want to notify on forbidden. */
export function notifyForbidden(errorOrPayload, fallback) {
  if (!isForbiddenError(errorOrPayload) && errorOrPayload?.response?.status !== 403) {
    return false;
  }
  showError(
    getNotificationErrorMessage(
      errorOrPayload,
      fallback || "You are not authorized to perform this action"
    )
  );
  return true;
}

/**
 * Attach a response interceptor that toasts on every non-2xx response.
 * Respects `config.skipErrorToast === true` to opt out (background polls, etc.).
 */
export function attachApiErrorToast(api, options = {}) {
  const { onUnauthorized, fallback = "Request failed" } = options;
  api.interceptors.response.use(
    (response) => Promise.resolve(response),
    (error) => {
      const status = error?.response?.status;
      if (status === 401 && typeof onUnauthorized === "function") {
        onUnauthorized(error);
      }
      if (!error?.config?.skipErrorToast) {
        notifyApiError(error, fallback);
      }
      return Promise.reject(error);
    }
  );
  return api;
}

/** Only call this after a confirmed successful (2xx) response. */
export function notifyApiSuccess(message) {
  if (message) showSuccess(message);
}

/**
 * Parse a failed `fetch` Response and toast status + API message (403/404/etc).
 * Returns a thrown Error with `.toastShown = true` so callers can rethrow safely.
 */
export async function notifyFetchResponseError(response, fallback = "Request failed") {
  let body = null;
  try {
    body = await response.json();
  } catch (_) {
    /* non-JSON body */
  }
  const payload = {
    status: response.status,
    data: body,
    message:
      (typeof body === "string" ? body : null) ||
      body?.message ||
      body?.error ||
      body?.detail ||
      fallback,
  };
  notifyApiError(payload, fallback);
  const err = new Error(payload.message || fallback);
  err.toastShown = true;
  err.status = response.status;
  err.data = body;
  return err;
}

/** True when HTTP status means the primary action failed (do not send notifications). */
export function isFailedHttpStatus(status) {
  return typeof status === "number" && status >= 400;
}
